import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";
import { TFunnelJourneyTrendResponse } from "@shoutout-labs/market_buzz_crm_types";

export class Utility {
  public static decodeToken(token: string): {
    user: { id: string; organizationId: string; role: string };
    subscription?: { status: string; packageId?: string; plan?: string };
    appInfo?: {
      appId: string;
      clientId: string;
      appName: string;
      createdAt: string;
    };
    metadata?: {
      authTimestamp: number;
      clientIp: string;
      userAgent: string;
      country: string;
      timezone: string;
    };
    iat: number;
    exp: number;
  } {
    const decoded: any = jwtDecode(token);
    // console.debug("decoded token:", JSON.stringify(decoded));
    // Check if this is a Clover JWT token (new format)
    if (
      decoded.organizationId &&
      decoded.merchantId &&
      decoded.role &&
      !decoded.user
    ) {
      // Transform Clover JWT to legacy format for compatibility
      return {
        user: {
          id: decoded.userId || decoded.merchantId, // Use userId if available, fallback to merchantId
          organizationId: decoded.organizationId,
          role: decoded.role
        },
        subscription: decoded.subscription || undefined,
        appInfo: decoded.appInfo || undefined,
        metadata: decoded.metadata || undefined,
        iat: decoded.iat,
        exp: decoded.exp
      };
    }

    // Return as is for legacy format
    return decoded;
  }
  public static getAvgSpend = (
    totalTransactionsCount: number,
    totalTransactionsSum: number
  ): number => {
    const avgSpend = totalTransactionsSum / totalTransactionsCount;
    return avgSpend;
  };

  static getMongoDBQuery(filters: any, config: any) {
    if (!filters || !filters.rules || !filters.rules.length) {
      return {};
    }

    const processRule = (rule: any) => {
      const { field, operator, value } = rule;
      const fieldConfig = config.fields[field];

      if (!fieldConfig) return {};

      switch (operator) {
        case "equal":
          return { [field]: value };
        case "not_equal":
          return { [field]: { $ne: value } };
        case "less":
          return { [field]: { $lt: value } };
        case "less_or_equal":
          return { [field]: { $lte: value } };
        case "greater":
          return { [field]: { $gt: value } };
        case "greater_or_equal":
          return { [field]: { $gte: value } };
        case "between":
          return { [field]: { $gte: value[0], $lte: value[1] } };
        case "contains":
          return { [field]: { $regex: value, $options: "i" } };
        case "not_contains":
          return { [field]: { $not: { $regex: value, $options: "i" } } };
        default:
          return {};
      }
    };

    const processRuleGroup = (group: any) => {
      if (!group.rules) return {};

      const conditions = group.rules
        .map((rule: any) => {
          if (rule.rules) {
            return processRuleGroup(rule);
          }
          return processRule(rule);
        })
        .filter((condition: any) => Object.keys(condition).length > 0);

      if (conditions.length === 0) return {};

      return {
        [group.conjunction.toLowerCase() === "and" ? "$and" : "$or"]: conditions
      };
    };

    return processRuleGroup(filters);
  }

  static replaceOrganizationCustomAttribute(
    text: string,
    organizationName?: string
  ): string {
    return text.replace(/\{\{organization\}\}/g, organizationName || "");
  }

  static hasCustomAttributes(text: string): boolean {
    const customAttributeRegex = /\{\{[^}]+\}\}/g;
    const squareBracketsRegex = /\[.*?\]/g;
    return customAttributeRegex.test(text) || squareBracketsRegex.test(text);
  }

  static formatDate(date: string | Date | undefined): string {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  }

  static isEmptyObject(object: any): boolean {
    return Object.keys(object).length === 0;
  }

  static isEqualObjects(obj1: any, obj2: any): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }
  static formatCurrency(amount: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(amount);
  }

  static fillMissingMonths(
    startDate: string,
    endDate: string,
    data: TFunnelJourneyTrendResponse["trends"]
  ): TFunnelJourneyTrendResponse["trends"] {
    const start = dayjs(startDate).startOf("month");
    const end = dayjs(endDate).endOf("month");
    const map = new Map(
      data.map((item: TFunnelJourneyTrendResponse["trends"][number]) => [
        item.month,
        item.metrics
      ])
    );

    const result: TFunnelJourneyTrendResponse["trends"] = [];
    let cursor = start.clone();

    while (cursor.isBefore(end) || cursor.isSame(end, "month")) {
      const monthKey = cursor.format("YYYY-MM");
      result.push({
        month: monthKey,
        metrics: map.get(monthKey) ?? {
          newCustomers: 0,
          firstPurchase: 0,
          secondVisit: 0,
          repeatBuyers: 0
        }
      });
      cursor = cursor.add(1, "month");
    }

    return result;
  }

  static convertTo24HourFormat(time: string): string {
    if (!time) return "";

    const [timePart, modifier] = time.split(" ");
    if (!timePart || !modifier) return time;

    let [hours, minutes] = timePart.split(":").map(Number);

    if (modifier.toUpperCase() === "PM" && hours < 12) hours += 12;
    if (modifier.toUpperCase() === "AM" && hours === 12) hours = 0;

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  }

  static convertTo12HourFormat(time: string): string {
    if (!time) return "";

    // Handle if time is already in 12-hour format
    if (time.includes("AM") || time.includes("PM")) {
      return time;
    }

    const [hours, minutes] = time.split(":").map(Number);

    // Validate input
    if (
      isNaN(hours) ||
      isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return "10:00 AM"; // Default fallback
    }

    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

    // Ensure proper formatting with leading zeros and exact spacing
    const formattedHours = displayHours.toString().padStart(2, "0");
    const formattedMinutes = minutes.toString().padStart(2, "0");

    return `${formattedHours}:${formattedMinutes} ${period}`;
  }
}
